import React, { useState, useEffect, Fragment } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import {
  ConnectAccountManagement,
  ConnectComponentsProvider,
} from "@stripe/react-connect-js";
import { backend } from "../../api";
import { toast, ToastContainer } from "react-toastify";

import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Paper,
} from "@mui/material";
import { useSelector } from "react-redux";
import PageBanner from "../PageBanner";
import useStripeConnect from "../../utils/useStripeConnect";
import { ClipLoader } from "react-spinners";

const PaymentMethodDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { currentUser } = useSelector((state) => state.auth);
  const [method, setMethod] = useState(null);
  const [loading, setLoading] = useState(true); // Loader state
  const [stripeConnectID, setstripeConnectID] = useState(null);
  const stripeConnectInstance = useStripeConnect(stripeConnectID);

  const fetchPaymentMethodDetails = async (id) => {
    setLoading(true); // Show loader when fetching starts
    try {
      const response = await backend.get(`/payment-method/${id}`);
      // const fetchedMethod = response.data.method;

      const fetchedMethod = response?.data?.paymentMethod?.[0]?.methods.find(
        (method) => method._id === id
      );

      if (fetchedMethod) {
        setMethod(fetchedMethod);

        if (fetchedMethod.stripeConnectId) {
          setstripeConnectID(fetchedMethod.stripeConnectId);
        }
      } else {
        toast.error("Payment method not found.");
      }
    } catch (error) {
      console.error("Error fetching payment method details:", error);
      toast.error("Failed to fetch payment method details.");
    } finally {
      setLoading(false); // Hide loader when fetching ends
    }
  };

  const handleResendVerificationEmail = async (method) => {
    try {
      const currentUrl = `${location.pathname}${location.search}`;
      const response = await backend.post("/payment-method/resend-email", {
        user: currentUser.author._id,
        accountNo: method.accountNo,
        receivingType: method.receivingType,
        currentUrl,
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to resend the verification email."
      );
    }
  };

  useEffect(() => {
    if (id) {
      fetchPaymentMethodDetails(id);
    }
  }, [id]);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        bgcolor="#f9f9f9"
      >
        <ClipLoader size={50} color="#123abc" />
        <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
          Loading Payment Method Details...
        </Typography>
      </Box>
    );
  }

  if (!method) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        bgcolor="#f9f9f9"
      >
        <Typography variant="h4" color="error" gutterBottom>
          No Payment Method Details Found
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          We couldn't locate the details for the selected payment method.
        </Typography>
        <Button
          onClick={() => history.goBack()}
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Fragment>
      <ToastContainer />
      <Box>
        <PageBanner
          title="Payment Method Details"
          image="/assets/images/bg4.jpg"
        />
        <Paper elevation={3} sx={{ p: 3 }}>
          <Divider sx={{ my: 2 }} />
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Account Number:
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    {method.accountNo || method.stripeConnectId || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Account Type:
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    {method.receivingType === "bank_account"
                      ? "Bank Account"
                      : "E-Money"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Verified:
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    {method.verified ? "Yes" : "No"}
                  </Typography>
                  {!method.verified && method.receivingType === "e_money" && (
                    <Box mt={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleResendVerificationEmail(method)}
                      >
                        Resend Verification Email
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          {stripeConnectInstance && (
            <Box mt={2}>
              <Typography variant="h6" gutterBottom>
                Account Management
              </Typography>
              <ConnectComponentsProvider
                connectInstance={stripeConnectInstance}
              >
                <ConnectAccountManagement
                  collectionOptions={{
                    fields: "eventually_due",
                    futureRequirements: "include",
                  }}
                />
              </ConnectComponentsProvider>
            </Box>
          )}
        </Paper>
      </Box>
    </Fragment>
  );
};

export default PaymentMethodDetails;
